import { useCallback, useEffect, useState } from "react"
import { 
  createOrUpdateService, 
  fetchServices, 
  deleteService,
  fetchAllUsers,
  createUser,
  updateUser,
  deleteUser
} from "./main-site-service.service"

export const useServicesQuery = (filters = {}) => {
  const [services, setServices] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const isError = Boolean(error)
  const { isFeatured, service_type } = filters

  const fetchContent = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const data = await fetchServices({ isFeatured, service_type })
      setServices(data || [])
    } catch (err) {
      console.error("Error fetching services:", err)
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }, [isFeatured, service_type])

  useEffect(() => {
    fetchContent()
  }, [fetchContent])

  return { services, isLoading, error, isError, refetch: fetchContent }
}

export const useServiceMutation = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const isError = Boolean(error)

  const mutate = useCallback(async (payload) => {
    try {
      setIsLoading(true)
      setError(null)
      setIsSuccess(false)

      const response = await createOrUpdateService(payload)
      setData(response)
      setIsSuccess(true)

      return response
    } catch (err) {
      console.error("Service mutation failed:", err)
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    mutate,
    data,
    isLoading,
    isSuccess,
    error,
    isError,
  }
}

export const useDeleteServiceMutation = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const isError = Boolean(error)

  const mutate = useCallback(async (serviceId) => {
    try {
      setIsLoading(true)
      setError(null)
      setIsSuccess(false)

      const response = await deleteService(serviceId)
      setIsSuccess(true)

      return response
    } catch (err) {
      console.error("Delete service failed:", err)
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    mutate,
    isLoading,
    isSuccess,
    error,
    isError,
  }
}

export const useAllUsersQuery = () => {
  const [users, setUsers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const isError = Boolean(error)

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const data = await fetchAllUsers()
      setUsers(data?.data || [])
    } catch (err) {
      console.error("Error fetching users:", err)
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return { users, isLoading, error, isError, refetch: fetchUsers }
}

export const useCreateUserMutation = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const isError = Boolean(error)

  const mutate = useCallback(async (userData) => {
    try {
      setIsLoading(true)
      setError(null)
      setIsSuccess(false)
      setData(null)

      const response = await createUser(userData)
      setData(response)
      setIsSuccess(true)

      return response
    } catch (err) {
      console.error("Create user failed:", err)
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setError(null)
    setData(null)
    setIsSuccess(false)
  }, [])

  return {
    mutate,
    data,
    isLoading,
    isSuccess,
    error,
    isError,
    reset,
  }
}

export const useUpdateUserMutation = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const isError = Boolean(error)

  const mutate = useCallback(async (userId, userData) => {
    try {
      setIsLoading(true)
      setError(null)
      setIsSuccess(false)
      setData(null)

      const response = await updateUser(userId, userData)
      setData(response)
      setIsSuccess(true)

      return response
    } catch (err) {
      console.error("Update user failed:", err)
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setError(null)
    setData(null)
    setIsSuccess(false)
  }, [])

  return {
    mutate,
    data,
    isLoading,
    isSuccess,
    error,
    isError,
    reset,
  }
}

export const useDeleteUserMutation = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const isError = Boolean(error)

  const mutate = useCallback(async (userId) => {
    try {
      setIsLoading(true)
      setError(null)
      setIsSuccess(false)

      const response = await deleteUser(userId)
      setIsSuccess(true)

      return response
    } catch (err) {
      console.error("Delete user failed:", err)
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    mutate,
    isLoading,
    isSuccess,
    error,
    isError,
  }
}
